import { useContext, useEffect, useMemo, useState } from 'react';
import { PaymentElement } from '@stripe/react-stripe-js';
import styled, { keyframes } from 'styled-components';
import { ANALYTICS_EVENT } from '@/common/constants/analyticsEvents';
import { THEME } from '@/common/styles/theme';
import { LanguageContext } from '@/common/contexts/LanguageContext';
import { useGuestAuth } from '@/common/contexts/GuestAuthContext';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { useAnalytics } from '@/common/contexts/AnalyticsContext';
import { formatTranslation } from '@/common/utils/formatTranslation';
import { getStripeCountryByLang } from '@/pages/customer/pawall/stripe/stripeCountryByLang';
import { STRIPE_PAYMENT_ELEMENT_OPTIONS } from '@/pages/customer/pawall/stripe/stripePaymentElementOptions';
import { useStripePaymentForm } from '../hooks/useStripePaymentForm';

const spin = keyframes`
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
`;

const ButtonSpinner = styled.span`
	display: inline-block;
	position: relative;
	z-index: 1;
	width: 18px;
	height: 18px;
	margin-right: 10px;
	border: 2px solid rgba(255, 255, 255, 0.28);
	border-top-color: #fff;
	border-radius: 50%;
	animation: ${spin} 0.75s linear infinite;
	vertical-align: middle;
`;

const btnShine = keyframes`
	0% {
		transform: translateX(-140%) skewX(-22deg);
		opacity: 0;
	}
	15% {
		opacity: 1;
	}
	85% {
		opacity: 1;
	}
	100% {
		transform: translateX(240%) skewX(-22deg);
		opacity: 0;
	}
`;

const btnGlow = keyframes`
	0%,
	100% {
		box-shadow:
			0 12px 36px rgba(255, 77, 242, 0.48),
			0 0 22px rgba(255, 77, 242, 0.28),
			0 0 0 1px rgba(255, 255, 255, 0.18) inset;
	}
	50% {
		box-shadow:
			0 16px 48px rgba(255, 77, 242, 0.68),
			0 0 42px rgba(255, 211, 107, 0.38),
			0 0 0 1px rgba(255, 255, 255, 0.28) inset;
	}
`;

const skeletonPulse = keyframes`
	0%,
	100% {
		opacity: 0.35;
	}
	50% {
		opacity: 0.7;
	}
`;

const FormContainer = styled.form`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-top: 0;
	min-width: 0;
	max-width: 100%;
	opacity: ${(props) => (props.$processing ? 0.72 : 1)};
	pointer-events: ${(props) => (props.$processing ? 'none' : 'auto')};
	transition: opacity 0.2s ease;
`;

const PaymentBlock = styled.div`
	display: grid;
	gap: 10px;
	min-width: 0;
	max-width: 100%;
`;

const PaymentElementShell = styled.div`
	position: relative;
	min-height: 300px;
	max-width: 100%;
	padding: 16px;
	border-radius: 16px;
	background: rgba(4, 2, 8, 0.45);
	border: 1px solid rgba(255, 255, 255, 0.07);
	box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
	overflow-x: hidden;
`;

const PaymentSkeleton = styled.div`
	position: absolute;
	inset: 16px;
	z-index: 2;
	display: grid;
	gap: 14px;
	align-content: start;
	pointer-events: none;
`;

const SkeletonTabs = styled.div`
	display: flex;
	gap: 8px;
`;

const SkeletonTab = styled.div`
	width: 52px;
	height: 38px;
	border-radius: 10px;
	background: rgba(255, 255, 255, 0.07);
	animation: ${skeletonPulse} 1.5s ease-in-out infinite;

	&:nth-child(1) {
		width: 58px;
		border: 1px solid rgba(255, 211, 107, 0.35);
		animation-delay: 0s;
	}

	&:nth-child(2) {
		animation-delay: 0.12s;
	}

	&:nth-child(3) {
		animation-delay: 0.24s;
	}

	&:nth-child(4) {
		animation-delay: 0.36s;
	}
`;

const SkeletonField = styled.div`
	display: grid;
	gap: 8px;
	animation: ${skeletonPulse} 1.5s ease-in-out infinite;

	&:nth-of-type(1) {
		animation-delay: 0.3s;
	}

	&:nth-of-type(2) {
		animation-delay: 0.42s;
	}

	&:nth-of-type(3) {
		animation-delay: 0.54s;
	}
`;

const SkeletonLabel = styled.div`
	width: 88px;
	height: 8px;
	border-radius: 999px;
	background: rgba(184, 148, 204, 0.35);
`;

const SkeletonInput = styled.div`
	height: 44px;
	border-radius: 10px;
	background: rgba(255, 255, 255, 0.06);
	border: 1px solid rgba(255, 255, 255, 0.08);
`;

const SkeletonFieldRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
`;

const PaymentElementWrap = styled.div`
	min-width: 0;
	max-width: 100%;
	opacity: ${(props) => (props.$ready ? 1 : 0)};
	transition: opacity 0.28s ease;
`;

const PayButton = styled.button`
	position: relative;
	overflow: hidden;
	isolation: isolate;
	background: ${THEME.gradientPrimary};
	color: #fff;
	border: none;
	padding: 18px 22px;
	font-family: ${THEME.fontApp};
	font-size: 1.02rem;
	font-weight: 800;
	letter-spacing: 0.03em;
	border-radius: 14px;
	cursor: pointer;
	transition:
		opacity 0.3s,
		transform 0.2s;
	width: 100%;
	animation: ${btnGlow} 2.2s ease-in-out infinite;

	&::before {
		content: '';
		position: absolute;
		top: -20%;
		bottom: -20%;
		left: 0;
		width: 42%;
		background: linear-gradient(
			90deg,
			transparent 0%,
			rgba(255, 255, 255, 0.08) 35%,
			rgba(255, 255, 255, 0.55) 50%,
			rgba(255, 255, 255, 0.08) 65%,
			transparent 100%
		);
		animation: ${btnShine} 2.4s ease-in-out infinite;
		pointer-events: none;
	}

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			180deg,
			rgba(255, 255, 255, 0.22) 0%,
			transparent 42%,
			rgba(0, 0, 0, 0.08) 100%
		);
		pointer-events: none;
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
		background: rgba(255, 255, 255, 0.08);
		animation: none;
		box-shadow: none;

		&::before,
		&::after {
			display: none;
		}
	}

	&:hover:not(:disabled) {
		transform: translateY(-2px);
	}

	span {
		position: relative;
		z-index: 1;
	}
`;

const ErrorMessage = styled.div`
	padding: 12px 14px;
	border-radius: 12px;
	border: 1px solid rgba(255, 122, 146, 0.35);
	background: rgba(255, 80, 110, 0.08);
	color: ${THEME.error};
	font-size: 0.82rem;
	text-align: center;
	line-height: 1.4;
`;

const SecureNote = styled.p`
	margin: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	color: ${THEME.soft};
	font-size: 0.72rem;
	line-height: 1.4;
	text-align: center;

	&::before {
		content: '🔒';
		font-size: 0.68rem;
		opacity: 0.85;
	}
`;

const LegalText = styled.p`
	margin: 0;
	font-size: 0.72rem;
	color: rgba(164, 120, 183, 0.95);
	text-align: center;
	line-height: 1.45;

	a {
		color: ${THEME.gold};
		text-decoration: underline;
		text-underline-offset: 2px;
	}
`;

const StripePaymentForm = ({
	selectedPlan,
	returnPath = '/studio',
	analyticsSource,
}) => {
	const t = useLanguageData('chat');
	const { lang } = useContext(LanguageContext);
	const { user } = useGuestAuth();
	const { trackAnalytics } = useAnalytics();
	const billingCountry = getStripeCountryByLang(lang);
	const [isPaymentReady, setIsPaymentReady] = useState(false);
	const { stripe, elements, isProcessing, errorMessage, handleSubmit } =
		useStripePaymentForm({
			selectedPlan,
			returnPath,
			billingCountry,
		});

	const handleFormSubmit = (event) => {
		if (analyticsSource) {
			trackAnalytics(ANALYTICS_EVENT.PLAN.CHECKOUT_CLICK, {
				planName: selectedPlan.name,
				source: analyticsSource,
			});
		}
		return handleSubmit(event);
	};

	const paymentElementOptions = useMemo(() => {
		const options = {
			...STRIPE_PAYMENT_ELEMENT_OPTIONS,
			defaultValues: {
				billingDetails: {
					address: {
						country: billingCountry,
					},
					...(user?.email && { email: user.email }),
				},
			},
		};

		return options;
	}, [billingCountry, user?.email]);

	useEffect(() => {
		setIsPaymentReady(false);
	}, [paymentElementOptions, selectedPlan.name]);

	const priceLabel = selectedPlan.price;

	return (
		<FormContainer onSubmit={handleFormSubmit} $processing={isProcessing}>
			<PaymentBlock>
				<PaymentElementShell>
					{!isPaymentReady && (
						<PaymentSkeleton aria-hidden="true">
							<SkeletonTabs>
								<SkeletonTab />
								<SkeletonTab />
								<SkeletonTab />
								<SkeletonTab />
							</SkeletonTabs>
							<SkeletonField>
								<SkeletonLabel />
								<SkeletonInput />
							</SkeletonField>
							<SkeletonFieldRow>
								<SkeletonField>
									<SkeletonLabel />
									<SkeletonInput />
								</SkeletonField>
								<SkeletonField>
									<SkeletonLabel />
									<SkeletonInput />
								</SkeletonField>
							</SkeletonFieldRow>
						</PaymentSkeleton>
					)}
					<PaymentElementWrap $ready={isPaymentReady}>
						<PaymentElement
							options={paymentElementOptions}
							onReady={() => setIsPaymentReady(true)}
						/>
					</PaymentElementWrap>
				</PaymentElementShell>
			</PaymentBlock>

			<PayButton
				type="submit"
				disabled={!stripe || !elements || isProcessing || !isPaymentReady}
				aria-busy={isProcessing}
			>
				{isProcessing && <ButtonSpinner aria-hidden="true" />}
				<span>
					{isProcessing
						? t.planCheckoutCreatingSubscription
						: formatTranslation(t.planCheckoutSubscribe, { price: priceLabel })}
				</span>
			</PayButton>

			<SecureNote>{t.planCheckoutSecureStripe}</SecureNote>

			<LegalText>
				{t.planCheckoutSubscribeTermsPrefix}{' '}
				<a
					href="/SoftAI regulamin terms of use.pdf"
					target="_blank"
					rel="noopener noreferrer"
				>
					{t.planCheckoutTermsLink}
				</a>{' '}
				{t.planCheckoutTermsAnd}{' '}
				<a href="/PRIVACY POLICY.pdf" target="_blank" rel="noopener noreferrer">
					{t.planCheckoutPrivacyLink}
				</a>
				.
			</LegalText>

			{errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
		</FormContainer>
	);
};

export default StripePaymentForm;
